/**
 * DefendantSection - Defendant name, DOB, age, SSN, occupation, income, address
 */

import { OathInput, OathSelect } from '../../../shared/OathUI';
import { STATE_OPTIONS } from '../../transferbonds/shared/usStates';
import CurrencyInput from '../shared/CurrencyInput';
import { LARGE_BOND_FIELD_LIMITS as LIMITS } from '../shared/largeBondConstants';

const DefendantSection = ({ formState, dispatch, disabled }) => {
  const set = (field) => (val) => dispatch({ type: 'SET_FIELD', field, value: val });

  const hasDob = !!formState.dob;
  const hasAge = !!formState.age?.trim();

  const handleDobChange = (val) => {
    dispatch({ type: 'SET_FIELD', field: 'dob', value: val });
    if (val) {
      const age = new Date().getFullYear() - new Date(val).getFullYear();
      dispatch({ type: 'SET_FIELD', field: 'age', value: age.toString() });
    }
  };

  return (
    <div>
      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Defendant Information</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <OathInput
          label="First Name"
          value={formState.firstName}
          onChange={set('firstName')}
          placeholder="First name"
          required
          disabled={disabled}
          maxLength={LIMITS.FIRST_NAME}
        />
        <OathInput
          label="Middle Initial"
          value={formState.middleInitial}
          onChange={(val) => dispatch({ type: 'SET_FIELD', field: 'middleInitial', value: val.slice(0, 1) })}
          placeholder="M.I."
          disabled={disabled}
          maxLength={LIMITS.MIDDLE_INITIAL}
        />
        <OathInput
          label="Last Name"
          value={formState.lastName}
          onChange={set('lastName')}
          placeholder="Last name"
          required
          disabled={disabled}
          maxLength={LIMITS.LAST_NAME}
        />
        <OathInput
          label="Date of Birth"
          type="date"
          value={formState.dob}
          onChange={handleDobChange}
          max={new Date().toISOString().split('T')[0]}
          required={!hasAge}
          helperText={!hasDob && !hasAge ? 'DOB or Age is required' : ''}
          disabled={disabled}
        />
        <OathInput
          label="Age"
          value={formState.age}
          onChange={(val) => dispatch({ type: 'SET_FIELD', field: 'age', value: val.replace(/\D/g, '') })}
          placeholder="Age"
          required={!hasDob}
          disabled={disabled}
        />
        <OathInput
          label="SSN"
          value={formState.ssn}
          onChange={(val) => {
            const digits = val.replace(/\D/g, '').slice(0, 9);
            let formatted = digits;
            if (digits.length > 5) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
            else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
            dispatch({ type: 'SET_FIELD', field: 'ssn', value: formatted });
          }}
          placeholder="XXX-XX-XXXX"
          disabled={disabled}
        />
        <OathInput
          label="Occupation"
          value={formState.occupation}
          onChange={set('occupation')}
          placeholder="Occupation"
          disabled={disabled}
          maxLength={LIMITS.OCCUPATION}
        />
        <CurrencyInput
          label="Income / Salary"
          value={formState.income}
          onChange={set('income')}
          disabled={disabled}
        />
      </div>

      <h4 className="font-medium text-gray-900 dark:text-white mt-4 mb-3">Defendant Address</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <div className="sm:col-span-2 md:col-span-3">
          <OathInput
            label="Street 1"
            value={formState.street1}
            onChange={set('street1')}
            placeholder="Street address"
            disabled={disabled}
            maxLength={LIMITS.STREET1}
          />
        </div>
        <div className="sm:col-span-2 md:col-span-3">
          <OathInput
            label="Street 2"
            value={formState.street2}
            onChange={set('street2')}
            placeholder="Apt, suite, etc."
            disabled={disabled}
            maxLength={LIMITS.STREET2}
          />
        </div>
        <OathInput
          label="City"
          value={formState.city}
          onChange={set('city')}
          placeholder="City"
          disabled={disabled}
          maxLength={LIMITS.CITY}
        />
        <OathSelect
          label="State"
          value={formState.state}
          onChange={set('state')}
          options={STATE_OPTIONS}
          placeholder="Select state..."
          disabled={disabled}
        />
        <OathInput
          label="Zip"
          value={formState.zip}
          onChange={(val) => {
            if (/^\d{0,5}$/.test(val)) {
              dispatch({ type: 'SET_FIELD', field: 'zip', value: val });
            }
          }}
          placeholder="5-digit zip"
          disabled={disabled}
          maxLength={LIMITS.ZIP}
        />
      </div>
    </div>
  );
};

export default DefendantSection;
